import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  getLocalStorage,
  setLocalStorage,
  getSystemTheme,
} from '@/lib/isomorphic'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'phonesalad-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // SSR-safe: use Vite's SSR flag for cleaner conditional logic
    if (import.meta.env.SSR) {
      return defaultTheme
    }
    return (getLocalStorage(storageKey) as Theme) || defaultTheme
  })

  // Hydration effect: sync theme from localStorage after initial render
  useEffect(() => {
    if (import.meta.env.SSR) return

    const storedTheme = getLocalStorage(storageKey) as Theme
    if (storedTheme && storedTheme !== theme) {
      setTheme(storedTheme)
    }
  }, [storageKey]) // Only run once after hydration

  useEffect(() => {
    // SSR-safe: skip DOM manipulation during server rendering
    if (import.meta.env.SSR) return

    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = getSystemTheme()

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      // SSR-safe: skip localStorage during server rendering
      if (!import.meta.env.SSR) {
        setLocalStorage(storageKey, theme)
      }
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
